import React, { useContext } from 'react';
import { Modal, Button, Icon, Header } from 'semantic-ui-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { EnvironmentContext } from '../Context/EnvironmentContext';
import './LowCreditsModal.css';

const LowCreditsModal = ({ isOpen, onClose, creditsNeeded = 10 }) => {
    const { t } = useTranslation();
    const env = useContext(EnvironmentContext);
    const currentCredits = env.userTokenCount || 0;
    const deficit = creditsNeeded - currentCredits;

    return (
        <AnimatePresence>
            {isOpen && (
                <Modal
                    open={isOpen}
                    onClose={onClose}
                    size="small"
                    className="low-credits-modal"
                >
                    <Modal.Content className="low-credits-content">
                        {/* Animated Icon */}
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: 'spring', stiffness: 200 }}
                            className="modal-icon"
                        >
                            <motion.div
                                animate={{ 
                                    rotate: [0, -10, 10, -10, 0],
                                }}
                                transition={{ duration: 0.5, repeat: 2 }}
                            >
                                😢
                            </motion.div>
                        </motion.div>

                        {/* Title */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            <Header as="h2" className="modal-title">
                                {t('lowCredits.title')}
                            </Header>
                        </motion.div>

                        {/* Current balance */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="balance-info"
                        >
                            <div className="balance-row">
                                <span className="label">{t('lowCredits.currentBalance')}</span>
                                <span className="value current">
                                    <Icon name="star" />
                                    {currentCredits.toLocaleString()}
                                </span>
                            </div>
                            <div className="balance-row">
                                <span className="label">{t('lowCredits.needed')}</span>
                                <span className="value needed">
                                    <Icon name="star" />
                                    {creditsNeeded.toLocaleString()}
                                </span>
                            </div>
                            <div className="balance-divider" />
                            <div className="balance-row deficit">
                                <span className="label">{t('lowCredits.missing')}</span>
                                <span className="value">
                                    <Icon name="star" />
                                    {deficit > 0 ? deficit.toLocaleString() : 0}
                                </span>
                            </div>
                        </motion.div>

                        {/* Message */}
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="modal-message"
                        >
                            {t('lowCredits.message')}
                        </motion.p>

                        {/* Quick offer */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                            className="quick-offer"
                        >
                            <div className="offer-badge">
                                <Icon name="bolt" /> {t('lowCredits.quickOffer')}
                            </div>
                            <div className="offer-details">
                                <span className="offer-credits">1,000 {t('Credits')}</span>
                                <span className="offer-price">0.99€</span>
                            </div>
                            <p className="offer-messages">
                                = 100 {t('lowCredits.messages')}
                            </p>
                        </motion.div>

                        {/* Actions */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="modal-actions"
                        >
                            <Button
                                as={Link}
                                to="/Buy"
                                primary
                                size="large"
                                className="buy-button"
                                onClick={onClose}
                            >
                                <Icon name="shopping cart" />
                                {t('lowCredits.buyCredits')}
                            </Button>
                            
                            <Button
                                basic
                                inverted
                                onClick={onClose}
                                className="later-button"
                            >
                                {t('lowCredits.later')}
                            </Button>
                        </motion.div>

                        {/* Daily reward reminder */}
                        {!env.user && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.7 }}
                                className="login-reminder"
                            >
                                <Icon name="gift" />
                                <span>
                                    {t('lowCredits.loginReminder')}
                                </span>
                                <Button 
                                    as={Link} 
                                    to="/login" 
                                    size="mini" 
                                    color="green"
                                    onClick={onClose}
                                >
                                    {t('LogIn')}
                                </Button>
                            </motion.div>
                        )}

                        {env.user && (
                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.7 }}
                                className="daily-reminder"
                            >
                                <Icon name="clock" />
                                {t('lowCredits.dailyReminder')}
                            </motion.p>
                        )}
                    </Modal.Content>
                </Modal>
            )}
        </AnimatePresence>
    );
};

export default LowCreditsModal;
